import { useMutation } from '@apollo/client'
import {
  Badge,
  Button,
  Copy,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input
} from '@chain-zen/ui'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { UPDATE_EMAIL_RATE_CONFIRMATION } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { hasDuplicateEmails, isEmail } from '../../../utils/common'
import type { TAppData } from '../apps-data'

interface IEmailRateConfirmationModalProps {
  app: TAppData
  isOpen: boolean
  onClose: () => void
  onDisabled: () => void
}

/**
 * Modal for managing email rate confirmation settings for an app
 */
const EmailRateConfirmationModal = React.memo((props: IEmailRateConfirmationModalProps) => {
  const { isOpen, onClose, app, onDisabled } = props
  const rateConfirmationEmail = app.workspaceApp?.appMetadata?.rateConfirmationEmail ?? ''
  const allowedEmailAddresses = app.workspaceApp?.appMetadata?.allowedEmailAddresses ?? []
  const [isEdit, setIsEdit] = useState(false)
  const [emailInput, setEmailInput] = useState<string>('')
  const [emails, setEmails] = useState<string[]>([])

  const handleCancel = () => {
    onClose()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setEmailInput(input)
    if (input.trim() !== '') {
      const parsedEmails = input.split(',').map((email) => email.trim())
      setEmails(parsedEmails)
    } else {
      setEmails([])
    }
  }

  const [updateEmailRateConfirmation, { loading }] = useMutation(UPDATE_EMAIL_RATE_CONFIRMATION, {
    onCompleted: () => {
      toast.success('Emails have been updated successfully')
      handleCancel()
    },
    onError: (err) => {
      toast.error(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
    },
    refetchQueries: [GET_APPS]
  })

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(rateConfirmationEmail)
    toast.success('Email address copied to clipboard')
  }

  const handleUpdate = () => {
    setEmails(allowedEmailAddresses)
    setEmailInput(allowedEmailAddresses.join(', '))
    setIsEdit(true)
  }

  const handleSaveDetails = () => {
    const invalidEmails = emails.filter((email) => !isEmail(email))

    if (emails.length === 0) {
      toast.error('Email addresses are required')
      return
    }

    if (invalidEmails.length > 0) {
      toast.error(`Invalid email addresses: ${invalidEmails.join(', ')}`)
      return
    }

    const isDuplicate = hasDuplicateEmails(emails)
    if (isDuplicate) {
      toast.error('Duplicate email addresses found')
      return
    }

    updateEmailRateConfirmation({ variables: { allowedEmailAddresses: emails, appType: app.appType } })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold leading-none tracking-tight">
            The {app.appName} app is currently enabled
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div>
            <div className="mb-2 font-semibold">Send rate confirmation email to:</div>
            <div className="flex items-center gap-2">
              <code className="rounded bg-muted px-2 py-1">{rateConfirmationEmail}</code>
              <Button variant="ghost" size="icon" onClick={handleCopyEmail}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div>
            <div className="mb-2 font-semibold">Emails that are allowed to send:</div>
            {isEdit ? (
              <div className="space-y-2">
                <Input
                  placeholder="Enter one or more emails separated by commas"
                  onChange={handleChange}
                  value={emailInput}
                />
                <div className="flex flex-wrap gap-2">
                  {emails.map((email, index) => {
                    const isValid = isEmail(email.trim())
                    if (!email.trim()) return null
                    return (
                      <Badge key={index} variant={isValid ? 'default' : 'destructive'}>
                        {email}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" onClick={handleUpdate}>
                {allowedEmailAddresses.map((email, index) => (
                  <Badge key={index} variant="default">
                    {email}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="default" onClick={isEdit ? handleSaveDetails : onDisabled} disabled={loading}>
            {isEdit ? 'Update' : 'Disable'}
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})

EmailRateConfirmationModal.displayName = 'EmailRateConfirmationModal'

export { EmailRateConfirmationModal }
